import { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDTGatewaysContext } from '../context/DTGatewaysContext';

const STALE_THRESHOLD_MS = 2 * 60 * 1000;

/**
 * Targeted Redux selector: only returns livedata items whose TagId is in the
 * provided Set. Using a factory + shallowEqual-style comparison means a widget
 * only re-renders when its own tag values change, not on every global livedata tick.
 */
function makeTagSelector(tagIdSet) {
  let prevResult = [];
  return (state) => {
    const livedata = state.mainpagedata.livedata;
    const next = livedata.filter((item) => tagIdSet.has(String(item.TagId)));
    // Stable reference: only replace if the matched set actually changed
    if (
      next.length === prevResult.length &&
      next.every((item, i) => item === prevResult[i])
    ) {
      return prevResult;
    }
    prevResult = next;
    return next;
  };
}

export function useDTWidgetData(tagprops, componentData) {
  const { gateways } = useDTGatewaysContext();
  const lastSeenRef = useRef({});
  const mountedAtRef = useRef(Date.now());

  // Build the tag ID set — stable as long as tagprops reference doesn't change
  const tagIdSet = useMemo(
    () => new Set((tagprops || []).map((tp) => String(tp.tagId ?? tp.tagid ?? tp.id))),
    [tagprops]
  );

  // Reset last-seen tracking when the bound tag set changes so removed tags
  // don't leak entries forever and added tags get a fresh grace window.
  useEffect(() => {
    const ids = Array.from(tagIdSet);
    const next = {};
    ids.forEach((id) => {
      if (lastSeenRef.current[id]) next[id] = lastSeenRef.current[id];
    });
    lastSeenRef.current = next;
    mountedAtRef.current = Date.now();
  }, [tagIdSet]);

  // Selector is recreated only when the set of tag IDs changes
  const selector = useMemo(() => makeTagSelector(tagIdSet), [tagIdSet]);
  const matchedData = useSelector(selector);

  const result = useMemo(() => {
    const now = Date.now();

    matchedData.forEach((item) => {
      lastSeenRef.current[item.TagId] = now;
    });

    const tagIds = Array.from(tagIdSet);
    // A tag is considered stale if it was seen at least once and then went
    // silent past the threshold, OR if it has never been seen and the widget
    // has been mounted long enough that we'd have expected at least one tick.
    const sinceMount = now - mountedAtRef.current;
    const isStale = tagIds.length > 0 && tagIds.some((id) => {
      const last = lastSeenRef.current[id];
      if (last) return now - last > STALE_THRESHOLD_MS;
      return sinceMount > STALE_THRESHOLD_MS;
    });

    const boundGatewayIds = new Set(
      (tagprops || []).map((tp) => tp.gatewayId).filter(Boolean).map(String)
    );
    const isDisconnected = [...boundGatewayIds].some((gwId) => {
      const gw = gateways.find((g) => String(g.GatewayID) === gwId);
      // Treat unknown/missing gateway state as not-disconnected to avoid
      // false negatives during the first status fetch.
      return gw && gw.connectionState && gw.connectionState !== 'Connected' && gw.connectionState !== 'Unknown';
    });

    const liveData = matchedData.map((item) => {
      const num = parseFloat(item.value);
      if (!isNaN(num)) {
        return { ...item, value: parseFloat(num.toFixed(2)) };
      }
      return item;
    });

    return {
      liveData,
      componentData,
      isStale,
      isDisconnected,
    };
  }, [matchedData, tagIdSet, tagprops, gateways, componentData]);

  return result;
}
